import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Pencil, Trash2, Loader2, Plus, Home, Check, ScanLine } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/hooks/useLanguage';
import { supabase } from '@/integrations/supabase/client';
import imageCompression from 'browser-image-compression';
import { Progress } from '@/components/ui/progress';

interface RecognizedUnit {
  id: string;
  name: string;
  notes?: string;
}

interface UnitWithNotes {
  name: string;
  notes?: string;
}

interface BulkUnitCreatorProps {
  open: boolean;
  onClose: () => void;
  onCreateUnits: (units: UnitWithNotes[]) => Promise<void>;
  houseId?: string;
  onCreateUnit?: (houseId: string, unitData: { name: string; notes?: string }, options?: { silent?: boolean }) => Promise<void>;
  onCreateUnitsBatch?: (houseId: string, units: Array<{ name: string; notes?: string }>, onProgress?: (created: number, total: number) => void) => Promise<unknown[]>;
  onNavigateToHouse?: () => void;
}

type CompletionState = 'idle' | 'creating' | 'completed';

export const BulkUnitCreator = ({ 
  open, 
  onClose, 
  onCreateUnits, 
  houseId, 
  onCreateUnit,
  onCreateUnitsBatch,
  onNavigateToHouse 
}: BulkUnitCreatorProps) => {
  const { language } = useLanguage();
  const [image, setImage] = useState<string | null>(null);
  const [recognizedUnits, setRecognizedUnits] = useState<RecognizedUnit[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [completionState, setCompletionState] = useState<CompletionState>('idle');
  const [creationProgress, setCreationProgress] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [createdUnits, setCreatedUnits] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Auto-open camera when dialog opens
  useEffect(() => {
    if (open && !image && completionState === 'idle') {
      setTimeout(() => {
        cameraInputRef.current?.click();
      }, 100);
    }
  }, [open, image, completionState]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setCompletionState('idle');
      setCreationProgress(0);
      setTotalUnits(0);
      setCreatedUnits(0);
    }
  }, [open]);

  const handleImageSelect = async (file: File) => {
    if (!file) return;

    try {
      console.log('📸 Original image size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      
      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.8
      };
      
      const compressedFile = await imageCompression(file, options);
      console.log('📸 Compressed image size:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');

      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target?.result as string;
        setImage(imageData);
        await performOCR(imageData);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      toast({
        title: "Fehler",
        description: "Bild konnte nicht verarbeitet werden",
        variant: "destructive"
      });
    }
  };

  const performOCR = async (imageData: string) => {
    setIsScanning(true);
    console.log('🔍 Starting doorbell image analysis...');
    
    try {
      console.log('📡 Calling AI edge function...');

      const extractEdgeErrorMessage = async (err: unknown): Promise<string> => {
        if (err && typeof err === 'object') {
          const anyErr = err as any;
          // Supabase FunctionsHttpError provides a Response in `context`
          const ctx = anyErr?.context;
          if (ctx && typeof ctx === 'object' && typeof (ctx as Response).clone === 'function') {
            try {
              const cloned = (ctx as Response).clone();
              const body = await cloned.json().catch(() => null);
              if (body && typeof body === 'object' && typeof (body as any).error === 'string') {
                return (body as any).error;
              }
            } catch {
              // ignore
            }
          }
          if (typeof anyErr?.message === 'string' && anyErr.message.trim()) return anyErr.message;
        }
        return 'Text konnte nicht erkannt werden';
      };

      const isRetriable = (err: unknown): boolean => {
        const msg = (err as any)?.message;
        if (typeof msg !== 'string') return false;
        const m = msg.toLowerCase();
        return m.includes('failed to fetch') || m.includes('network') || m.includes('timeout');
      };

      const invokeAnalyze = async (attempt: number): Promise<{ data: any; error: any }> => {
        const res = await supabase.functions.invoke('analyze-doorbell-image', {
          body: { imageData, language }
        });

        if (res.error && attempt === 1 && isRetriable(res.error)) {
          console.warn('⚠️ analyze-doorbell-image failed, retrying once…', res.error);
          await new Promise(resolve => setTimeout(resolve, 900));
          return await invokeAnalyze(2);
        }

        return res;
      };

      const { data, error } = await invokeAnalyze(1);

      console.log('📥 Response received:', { data, error });

      if (error) {
        console.error('❌ Edge function error:', error);
        // Throw with a more helpful message if possible
        const msg = await extractEdgeErrorMessage(error);
        throw new Error(msg);
      }

      if (!data || !data.units) {
        console.error('❌ Invalid response:', data);
        throw new Error('Keine Wohneinheiten in der Antwort gefunden');
      }

      console.log('✅ Units extracted:', data.units);

      const units: RecognizedUnit[] = data.units
        .map((unitData: string | string[]) => {
          if (typeof unitData === 'string') {
            const trimmed = unitData.trim();
            if (!trimmed) return null;
            return {
              id: crypto.randomUUID(),
              name: trimmed
            };
          }
          
          if (Array.isArray(unitData)) {
            const validNames = unitData
              .filter(name => name && name.trim().length > 0)
              .map(name => name.trim());
            
            if (validNames.length === 0) return null;
            
            return {
              id: crypto.randomUUID(),
              name: validNames.join(' / ')
            };
          }
          
          return null;
        })
        .filter(unit => unit !== null) as RecognizedUnit[];

      if (units.length === 0) {
        throw new Error('Keine Wohneinheiten erkannt');
      }

      setRecognizedUnits(prev => [...prev, ...units]);
      
      toast({
        title: "Text erkannt",
        description: `${units.length} Wohneinheiten erkannt`,
      });
    } catch (error) {
      console.error('❌ OCR Error:', error);
      
      let errorMessage = 'Text konnte nicht erkannt werden';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Fehler",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsScanning(false);
      console.log('🏁 Analysis complete');
    }
  };

  const handleEdit = (unit: RecognizedUnit) => {
    setEditingId(unit.id);
    setEditValue(unit.name);
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    
    setRecognizedUnits(prev =>
      prev.map(unit =>
        unit.id === editingId ? { ...unit, name: editValue } : unit
      )
    );
    setEditingId(null);
    setEditValue('');
  };

  const handleDelete = (id: string) => {
    setRecognizedUnits(prev => prev.filter(unit => unit.id !== id));
  };

  const handleAddManual = () => {
    const newUnit: RecognizedUnit = {
      id: crypto.randomUUID(),
      name: ''
    };
    setRecognizedUnits(prev => [...prev, newUnit]);
    setEditingId(newUnit.id);
    setEditValue('');
  };

  const handleCreateUnits = async () => {
    const validUnits = recognizedUnits.filter(unit => unit.name.trim().length > 0);
    
    if (validUnits.length === 0) {
      toast({
        title: "Keine Wohneinheiten",
        description: "Bitte fügen Sie mindestens eine Wohneinheit hinzu",
        variant: "destructive"
      });
      return;
    }

    setCompletionState('creating');
    setTotalUnits(validUnits.length);
    setCreatedUnits(0);
    setCreationProgress(0);

    try {
      const unitsWithNotes: UnitWithNotes[] = validUnits.map(unit => ({
        name: unit.name,
        notes: unit.notes
      }));
      
      if (houseId && onCreateUnitsBatch) {
        // Use optimized batch creation
        console.log(`⚡ Creating ${unitsWithNotes.length} units with batch insert...`);
        
        await onCreateUnitsBatch(
          houseId, 
          unitsWithNotes,
          (created, total) => {
            setCreatedUnits(created);
            setCreationProgress(Math.round((created / total) * 100));
          }
        );
        
        console.log(`✅ Batch creation complete`);
        setCompletionState('completed');
        setRecognizedUnits([]);
        setImage(null);
      } else if (houseId && onCreateUnit) {
        // Fallback to one-by-one creation
        console.log(`⚡ Creating ${unitsWithNotes.length} units one by one...`);
        
        for (let i = 0; i < unitsWithNotes.length; i++) {
          await onCreateUnit(houseId, {
            name: unitsWithNotes[i].name,
            notes: unitsWithNotes[i].notes
          }, { silent: true });
          
          const completed = i + 1;
          setCreatedUnits(completed);
          setCreationProgress(Math.round((completed / validUnits.length) * 100));
        }
        
        console.log(`✅ Bulk creation complete`);
        setCompletionState('completed');
        setRecognizedUnits([]);
        setImage(null);
      } else {
        await onCreateUnits(unitsWithNotes);
        setImage(null);
        setRecognizedUnits([]);
        setTimeout(() => {
          onClose();
        }, 100);
      }
    } catch (error) {
      console.error('Error creating units:', error);
      toast({
        title: "Fehler",
        description: "Wohneinheiten konnten nicht erstellt werden",
        variant: "destructive"
      });
      setCompletionState('idle');
    }
  };

  const handleAddMore = () => {
    setCompletionState('idle');
    setImage(null);
    // Open camera again
    setTimeout(() => {
      cameraInputRef.current?.click();
    }, 100);
  };

  const handleFinish = () => {
    toast({
      title: "Erfolgreich!",
      description: `${totalUnits} Wohneinheit${totalUnits > 1 ? 'en' : ''} wurde${totalUnits > 1 ? 'n' : ''} hinzugefügt`
    });
    onClose();
  };

  const handleGoToHouse = () => {
    toast({
      title: "Erfolgreich!",
      description: `${totalUnits} Wohneinheit${totalUnits > 1 ? 'en' : ''} wurde${totalUnits > 1 ? 'n' : ''} hinzugefügt`
    });
    onClose();
    if (onNavigateToHouse) {
      onNavigateToHouse();
    }
  };

  const handleReset = () => {
    setImage(null);
    setEditingId(null);
    setEditValue('');
  };

  // Progress Overlay during creation
  if (completionState === 'creating') {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center justify-center py-8 space-y-6">
            <div className="relative">
              <Loader2 className="h-16 w-16 animate-spin text-primary" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold">{creationProgress}%</span>
              </div>
            </div>
            
            <div className="text-center space-y-2">
              <p className="text-lg font-medium">Wohneinheiten werden erstellt</p>
              <p className="text-sm text-muted-foreground">
                {createdUnits} von {totalUnits} erstellt
              </p>
            </div>
            
            <Progress value={creationProgress} className="w-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Completion Dialog
  if (completionState === 'completed') {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center justify-center py-6 space-y-6">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            
            <div className="text-center space-y-2">
              <p className="text-lg font-medium">
                {totalUnits} Wohneinheit{totalUnits > 1 ? 'en' : ''} erstellt
              </p>
              <p className="text-sm text-muted-foreground">
                Weitere Wohneinheiten zur Hausnummer hinzufügen?
              </p>
            </div>
            
            <div className="flex flex-col w-full gap-3">
              <Button
                onClick={handleAddMore}
                variant="outline"
                className="w-full gap-2"
              >
                <ScanLine className="h-4 w-4" />
                Weitere scannen
              </Button>
              
              <Button
                onClick={handleGoToHouse}
                className="w-full gap-2"
              >
                <Home className="h-4 w-4" />
                Zum Haus
              </Button>
              
              <Button
                onClick={handleFinish}
                variant="ghost"
                className="w-full"
              >
                Fertig
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main Dialog
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Wohneinheiten von Klingelschild scannen</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Upload Section */}
          {!image && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                className="h-32 flex-col gap-2 w-full max-w-xs"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-8 w-8" />
                <span>Kamera</span>
              </Button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files && handleImageSelect(e.target.files[0])}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => e.target.files && handleImageSelect(e.target.files[0])}
          />

          {/* Image Preview */}
          {image && (
            <Card className="p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Klingelaufnahme:</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                  >
                    Neu Scannen
                  </Button>
                </div>
                <img
                  src={image}
                  alt="Klingelschild"
                  className="w-full max-w-sm mx-auto rounded-lg border"
                />
              </div>
            </Card>
          )}

          {/* Scanning Progress */}
          {isScanning && (
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Text wird erkannt...</span>
              </div>
            </Card>
          )}

          {/* Recognized Units List */}
          {recognizedUnits.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Erkannte Wohneinheiten ({recognizedUnits.length})</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddManual}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Manuell hinzufügen
                </Button>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recognizedUnits.map((unit) => (
                  <Card key={unit.id} className="p-3">
                    {editingId === unit.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Name der Wohneinheit"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') {
                              setEditingId(null);
                              setEditValue('');
                            }
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleSaveEdit}
                        >
                          Speichern
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{unit.name || '(Leer)'}</span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(unit)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(unit.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateUnits}
            disabled={recognizedUnits.length === 0}
            className="flex-1"
          >
            {recognizedUnits.length} hinzufügen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
